import { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { CSVLink, Flex, StyledFormSelect, WrappedQuery } from '../';
import {
  ALL_INTERVENTIONS,
  ALL_SERVICE_PROVIDERS,
  ALL_STAKEHOLDER_TYPES,
  EXPORT_TREATMENT_GROUPS,
  FILTERED_PERSONNEL_POSITIONS,
  INSTITUTIONS_FILTERED,
} from '../../../graphql';
import { formatSelectValue, formatStakeholdersProfileData, getPortal, sanitizeData } from '../../../utils';
import { stakeholdersHeaders } from '../../../consts';
import { getAllProgrammesQuery } from '../../../containers/shared/programmes/utils';

type Props = {
  serviceProvider: string;
  school: string;
  intervention: string;
  stakeholderType: string;
  programme: string;
  personnelPosition: string;
};

type State = {
  programmeIds?: string[];
  serviceProviderIds?: string[];
  interventionIds?: string[];
  schoolIds?: string[];
  stakeholderType?: { label: string; value: string };
  personnelPositionIds?: string[];
};

const StakeholderExport = ({
  programme,
  stakeholderType,
  school,
  intervention,
  personnelPosition,
  serviceProvider,
}: Props) => {
  const [portal] = useState(getPortal());
  const [state, setState] = useState<State>({});
  const [institutions, setInstitutions] = useState<[{ node: { id: string } }] | any>([]);

  const { data: programmeData, error: programmeError, loading: programmeDataLoading } = useQuery(
    getAllProgrammesQuery(portal),
    {
      variables: {
        input: {
          isArchived: false,
          includeWhereReporter: true,
        },
      },
    }
  );
  const { data: spData, error: spError, loading: spDataLoading } = useQuery(ALL_SERVICE_PROVIDERS, {
    variables: {
      input: {
        ...(state.programmeIds && { programmeIds: state.programmeIds }),
      },
    },
  });
  const { data: interventionData, error: interventionError, loading: interventionDataLoading } = useQuery(
    ALL_INTERVENTIONS,
    {
      variables: {
        input: {
          ...(state.programmeIds && { programmeIds: state.programmeIds }),
          ...(state.serviceProviderIds && { serviceProviderIds: state.serviceProviderIds }),
        },
      },
    }
  );
  const {
    data: institutionData,
    fetchMore: fetchMoreInstitutions,
    error: institutionError,
    loading: institutionLoading,
  } = useQuery(INSTITUTIONS_FILTERED, {
    variables: {
      input: {
        first: 10,
        ...(state.programmeIds && { programmeIds: state.programmeIds }),
        ...(state.interventionIds && { interventionIds: state.interventionIds }),
      },
    },
  });
  const { data: stakeholderTypeData, error: stakeholderTypeError, loading: stakeholderTypeLoading } = useQuery(
    ALL_STAKEHOLDER_TYPES
  );
  const { data: personnelPositionData, error: personnelPositionError, loading: personnelPositionLoading } = useQuery(
    FILTERED_PERSONNEL_POSITIONS,
    {
      variables: {
        input: {
          ...(state.stakeholderType && { typeId: state.stakeholderType.value }),
        },
      },
    }
  );

  useEffect(() => {
    if (institutionData && institutionData.institutions && institutionData.institutions.edges) {
      setInstitutions(institutionData.institutions.edges);
    }
  }, [institutionData]);

  return (
    <Flex size="1" direction="column">
      <Flex size="1" direction="row" marginRight="10px">
        <StyledFormSelect
          isMulti={true}
          isClearable={true}
          name="programmeIds"
          label="Programme"
          placeholder={programmeDataLoading ? 'Data loading, Please wait' : 'Data ready, Please select option'}
          isDisabled={programmeDataLoading}
          onChange={(option: any[]) => {
            const newState = Object.assign({}, state, {
              [programme]: option ? option.map(opt => opt.value) : undefined,
            });
            setState(newState);
          }}
          options={
            programmeDataLoading || programmeError || !(programmeData && programmeData.programmes)
              ? []
              : programmeData.programmes.map((item: any) =>
                  formatSelectValue({
                    label: item.name,
                    value: item.id,
                  })
                )
          }
        />
      </Flex>
      <Flex size="1" direction="row" marginRight="10px">
        <StyledFormSelect
          isMulti={true}
          isClearable={true}
          name="serviceProviderIds"
          label="Service Provider"
          placeholder={spDataLoading ? 'Data loading, Please wait' : 'Data ready, Please select option'}
          isDisabled={spDataLoading}
          onChange={(option: any[]) => {
            const newState = Object.assign({}, state, {
              [serviceProvider]: option ? option.map(opt => opt.value) : undefined,
            });
            setState(newState);
          }}
          options={
            spDataLoading || spError || !(spData && spData.serviceProviders)
              ? []
              : spData.serviceProviders.map((item: any) =>
                  formatSelectValue({
                    label: item.name,
                    value: item.id,
                  })
                )
          }
        />
      </Flex>
      <Flex size="1" direction="row" marginRight="10px">
        <StyledFormSelect
          isMulti={true}
          isClearable={true}
          name="interventionIds"
          label="Intervention"
          placeholder={interventionDataLoading ? 'Data loading, Please wait' : 'Data ready, Please select option'}
          isDisabled={interventionDataLoading}
          onChange={(option: any[]) => {
            const newState = Object.assign({}, state, {
              [intervention]: option ? option.map(opt => opt.value) : undefined,
            });
            setState(newState);
          }}
          options={
            interventionDataLoading || interventionError || !(interventionData && interventionData.allInterventions)
              ? []
              : interventionData.allInterventions.map((item: any) =>
                  formatSelectValue({
                    label: item.name,
                    value: item.id,
                  })
                )
          }
        />
      </Flex>
      <Flex size="1" direction="row" marginRight="10px">
        <StyledFormSelect
          isMulti={true}
          isClearable={true}
          name="schoolIds"
          label="Institution"
          placeholder={institutionLoading ? 'Data loading, Please wait' : 'Data ready, Please select option'}
          isDisabled={institutionLoading}
          onChange={(option: any[]) => {
            if (!option) {
              setState({ [school]: undefined });
            } else {
              const newState = Object.assign({}, state, { [school]: option.map(opt => opt.value) });
              setState(newState);
            }
          }}
          options={
            institutionLoading || institutionError || !Boolean(institutions.length)
              ? []
              : institutions.map((item: any) =>
                  formatSelectValue({
                    label: item.node.name,
                    value: item.node.id,
                  })
                )
          }
          scrollHandler={async () => {
            // call the next page of the filteredInsitutions query
            return (
              institutionData.institutions &&
              institutionData.institutions.pageInfo &&
              institutionData.institutions.pageInfo.hasNextPage &&
              fetchMoreInstitutions({
                variables: {
                  input: {
                    first: 10,
                    skip: institutions.length,
                    ...(state.programmeIds && { programmeIds: state.programmeIds }),
                    ...(state.interventionIds && { interventionIds: state.interventionIds }),
                  },
                },
              }).then(({ data }: any) => {
                if (data && data.institutions && data.institutions.edges) {
                  const newInstitutions = [...institutions, ...data.institutions.edges];
                  setInstitutions(newInstitutions);
                }
              })
            );
          }}
        />
      </Flex>
      <Flex size="1" direction="row" marginRight="10px">
        <StyledFormSelect
          isClearable={true}
          name="stakeholderType"
          label="Stakeholder Type"
          placeholder={stakeholderTypeLoading ? 'Data loading, Please wait' : 'Data ready, Please select option'}
          isDisabled={stakeholderTypeLoading}
          onChange={(option: any) => {
            const newState = Object.assign({}, state, {
              [stakeholderType]: option,
            });
            setState(newState);
          }}
          options={
            stakeholderTypeLoading ||
            stakeholderTypeError ||
            !(stakeholderTypeData && stakeholderTypeData.stakeholderTypes)
              ? []
              : stakeholderTypeData.stakeholderTypes.map((item: any) =>
                  formatSelectValue({ label: item.name, value: item.id })
                )
          }
        />
      </Flex>
      <Flex size="1" direction="row" marginRight="10px">
        <StyledFormSelect
          isClearable={true}
          isMulti={true}
          name="personnelPositionIds"
          label="Personnel Position"
          placeholder={personnelPositionLoading ? 'Data loading, Please wait' : 'Data ready, Please select option'}
          isDisabled={personnelPositionLoading}
          onChange={(options: any[]) => {
            const newState = Object.assign({}, state, {
              [personnelPosition]: options && options.map(opt => opt.value),
            });
            setState(newState);
          }}
          options={
            personnelPositionLoading ||
            personnelPositionError ||
            !(personnelPositionData && personnelPositionData.filteredPersonnelPositions)
              ? []
              : personnelPositionData.filteredPersonnelPositions.map((item: any) =>
                  formatSelectValue({ label: item.name, value: item.id })
                )
          }
        />
      </Flex>

      {state.stakeholderType ? (
        <WrappedQuery
          query={EXPORT_TREATMENT_GROUPS}
          variables={{
            input: {
              first: 100,
              ...(state.interventionIds && { interventionIds: state.interventionIds }),
              ...(state.serviceProviderIds && { serviceProviderIds: state.serviceProviderIds }),
              ...(state.programmeIds && { programmeIds: state.programmeIds }),
              ...(state.schoolIds && { schoolIds: state.schoolIds }),
              ...(state.stakeholderType && { stakeholderTypeId: state.stakeholderType.value }),
              ...(state.personnelPositionIds && { personnelPositionIds: state.personnelPositionIds }),
            },
          }}
          overrideStates={true}
        >
          {({ loading, error, data, fetchMore }) => {
            if (loading) {
              return <div>Preparing data for export...</div>;
            }

            if (error) {
              return <div>Something went wrong</div>;
            }

            // call the next page of the exportTreatmentGroups query
            data.stakeholders &&
              data.stakeholders.pageInfo &&
              data.stakeholders.pageInfo.hasNextPage &&
              fetchMore({
                variables: {
                  input: {
                    first: 10,
                    ...(state.interventionIds && { interventionIds: state.interventionIds }),
                    ...(state.serviceProviderIds && { serviceProviderIds: state.serviceProviderIds }),
                    ...(state.programmeIds && { programmeIds: state.programmeIds }),
                    ...(state.schoolIds && { schoolIds: state.schoolIds }),
                    ...(state.stakeholderType && { stakeholderTypeId: state.stakeholderType.value }),
                    ...(state.personnelPositionIds && { personnelPositionIds: state.personnelPositionIds }),
                    after: data.stakeholders && data.stakeholders.pageInfo && data.stakeholders.pageInfo.endCursor,
                  },
                },
                updateQuery: (previousResult, { fetchMoreResult }) => {
                  const newStakeholders = fetchMoreResult.stakeholders.edges;
                  const previousStakeholders = previousResult.stakeholders ? previousResult.stakeholders.edges : [];
                  return {
                    stakeholders: {
                      aggregate: fetchMoreResult.stakeholders.aggregate,
                      pageInfo: fetchMoreResult.stakeholders.pageInfo,
                      edges: [...previousStakeholders, ...newStakeholders],
                      __typename: fetchMoreResult.stakeholders.__typename,
                    },
                  };
                },
              });

            if (data.stakeholders && data.stakeholders.pageInfo && data.stakeholders.pageInfo.hasNextPage) {
              return <div>Fetching more pages...</div>;
            }

            const formatted: any[] =
              data.stakeholders && data.stakeholders.edges
                ? formatStakeholdersProfileData({ data: data.stakeholders.edges })
                : [];

            const dateFormats = {
              dateOfBirth: 'yyyy-MM-dd',
              dateExited: 'yyyy-MM-dd',
            };
            const sanitized = formatted.length ? sanitizeData(formatted, dateFormats) : [];
            return (
              <Flex size="1" direction="row" marginRight="10px">
                {sanitized && sanitized.length > 0 && (
                  <CSVLink
                    filename={`StakeholdersSPExport-${new Date()}.csv`}
                    headers={stakeholdersHeaders[(state.stakeholderType && state.stakeholderType.label) || 'Learners']}
                    data={sanitized}
                  >
                    Export CSV
                  </CSVLink>
                )}

                {sanitized && sanitized.length < 1 && <span>No stakeholders data for given parameters</span>}
              </Flex>
            );
          }}
        </WrappedQuery>
      ) : (
        <div>No Stakeholder Type Selected</div>
      )}
    </Flex>
  );
};

export default StakeholderExport;
